import type { ID } from '@octopus-do/octopus-project-core'

import {
  LAST_WORKSPACE_KEY,
  LAST_WORKSPACE_TTL,
  PERSONAL_WORKSPACE_ID,
} from '../constants'
import type { Workspace } from '../types'

interface LastWorkspaceRecord {
  workspaceId: ID
  updatedAt: number
}

type LastWorkspaceRecords = Record<ID, LastWorkspaceRecord>

const isRecordAlive = (record: LastWorkspaceRecord, now: number): boolean =>
  now - record.updatedAt < LAST_WORKSPACE_TTL

const readRecords = (): LastWorkspaceRecords => {
  try {
    const parsed = JSON.parse(localStorage.getItem(LAST_WORKSPACE_KEY) ?? '')

    // до OCT-4785 под этим ключом лежала плоская строка с id пространства
    if (typeof parsed !== 'object' || parsed === null) {
      return {}
    }

    return parsed
  } catch {
    return {}
  }
}

const omitDeadRecords = (
  records: LastWorkspaceRecords,
  now: number
): LastWorkspaceRecords =>
  Object.fromEntries(
    Object.entries(records).filter(([, record]) => isRecordAlive(record, now))
  )

export const saveLastWorkspaceId = (userId: ID, workspaceId: ID): void => {
  const now = Date.now()

  const records: LastWorkspaceRecords = {
    ...omitDeadRecords(readRecords(), now),
    [userId]: { workspaceId, updatedAt: now },
  }

  localStorage.setItem(LAST_WORKSPACE_KEY, JSON.stringify(records))
}

export const getLastWorkspaceId = (userId: ID): ID | null => {
  const record = readRecords()[userId] ?? null

  if (record === null || !isRecordAlive(record, Date.now())) {
    return null
  }

  return record.workspaceId
}

export const pickDefaultWorkspaceId = (
  workspaces: Workspace[],
  lastWorkspaceId: ID | null
): ID => {
  const lastWorkspace = workspaces.find(
    (w) => w.id === lastWorkspaceId && !w.expired
  )

  if (lastWorkspace) {
    return lastWorkspace.id
  }

  const teamWorkspace = workspaces.find((w) => w.type === 'team' && !w.expired)

  return teamWorkspace ? teamWorkspace.id : PERSONAL_WORKSPACE_ID
}
